import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import Moment from 'react-moment';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableHead from '@material-ui/core/TableHead';
import TableCell from '@material-ui/core/TableCell';
import Chip from '@material-ui/core/Chip';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import TableSortLabel from '@material-ui/core/TableSortLabel';

import API from '../api/ApiTool';
import Loading from '../components/Loading';
import { mid } from '../theme';

const BUCKETS = [
  {
    key: 'all',
    label: 'All Numbers',
    color: mid.blue,
    bg: 'rgba(37,99,235,0.12)',
    desc: 'All numbers we are paying for',
  },
  {
    key: 'healthy',
    label: 'Healthy',
    color: mid.green,
    bg: 'rgba(22,163,74,0.12)',
    desc: 'Subscribed, on a plan, billing current (≤90 days)',
  },
  {
    key: 'watch',
    label: 'Watch',
    color: mid.amber,
    bg: 'rgba(180,83,9,0.12)',
    desc: 'Subscribed but no billing activity in 90+ days',
  },
  {
    key: 'grace',
    label: 'Grace Period',
    color: mid.indigo,
    bg: 'rgba(79,70,229,0.12)',
    desc: 'Unsubscribed but within 30-day grace window',
  },
  {
    key: 'cleanup',
    label: 'Cleanup Ready',
    color: mid.red,
    bg: 'rgba(220,38,38,0.12)',
    desc: 'No valid assignment — ready to release',
  },
  {
    key: 'held',
    label: 'Held',
    color: mid.cyan,
    bg: 'rgba(8,145,178,0.12)',
    desc: 'Deliberately held — excluded from cleanup',
  },
];

const BUCKET_CHIP = {
  healthy: { label: 'Healthy',  color: mid.green },
  watch:   { label: 'Watch',    color: mid.amber },
  grace:   { label: 'Grace',    color: mid.indigo },
  cleanup: { label: 'Cleanup',  color: mid.red },
  held:    { label: 'Held',     color: mid.cyan },
};

function bucketForRow(row) {
  if (row.held) return 'held';
  if (row.deleted || row.bridge_deleted || !row.company) return 'cleanup';
  const { status, tariff, current_period_end } = row.company;
  const now = Date.now();
  const lastPaid = row.last_paid ? new Date(row.last_paid).getTime() : 0;
  const periodEnd = current_period_end ? new Date(current_period_end).getTime() : 0;
  const graceCutoff = now - 30 * 86400000;
  const staleCutoff = now - 90 * 86400000;
  if (status === 'subscribed' && tariff && lastPaid >= staleCutoff) return 'healthy';
  if (status === 'subscribed' && lastPaid < staleCutoff) return 'watch';
  if (status === 'not-subscribed' && periodEnd >= graceCutoff) return 'grace';
  return 'cleanup';
}

function planLabel(tariff) {
  if (!tariff) return 'no plan';
  if (tariff.match(/^1-500-/)) return 'Free Trial';
  return tariff;
}

const styles = {
  page: {
    backgroundColor: mid.bg0,
    minHeight: '100vh',
    padding: 0,
  },
  header: {
    backgroundColor: mid.bg1,
    borderBottom: `1px solid ${mid.border}`,
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: mid.text0,
    letterSpacing: '-0.01em',
  },
  bucketBar: {
    display: 'flex',
    gap: '12px',
    padding: '16px 24px',
    backgroundColor: mid.bg0,
    borderBottom: `1px solid ${mid.border}`,
    flexWrap: 'wrap',
  },
  bucketCard: (active, bucket) => ({
    flex: '1 1 140px',
    minWidth: '120px',
    padding: '12px 16px',
    borderRadius: '8px',
    border: `1px solid ${active ? bucket.color : mid.border}`,
    backgroundColor: active ? bucket.bg : mid.bg1,
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
  }),
  bucketCount: (color) => ({
    fontSize: '24px',
    fontWeight: 700,
    color: color,
    lineHeight: 1.1,
  }),
  bucketLabel: {
    fontSize: '11px',
    color: mid.text1,
    marginTop: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  bucketDesc: {
    fontSize: '10px',
    color: mid.text2,
    marginTop: '4px',
  },
  tableWrap: {
    backgroundColor: mid.bg1,
  },
  empty: {
    padding: '48px',
    textAlign: 'center',
    color: mid.text2,
    fontSize: '13px',
  },
  loadingMore: {
    padding: '12px 24px',
    color: mid.text1,
    fontSize: '12px',
  },
};

class NumberAssignment extends Component {
  constructor(props) {
    super(props);
    this.state = {
      summary: null,
      numbers: [],
      bucket: 'all',
      offset: 0,
      total: 0,
      loading: true,
      loadingMore: false,
      property: 'created',
      order: 'desc',
      selected: new Set(),
      releasing: false,
      holding:   false,
      holdDialogOpen: false,
      holdReason: 'test',
      holdComment: '',
      loadMoreCount: 0,
    };

    window.onscroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop
        === document.documentElement.offsetHeight
      ) {
        if (!this.state.loadingMore && this.state.numbers.length < this.state.total) {
          this.fetchPage(this.state.offset, false);
        }
      }
    };
  }

  componentDidMount() {
    this.fetchPage(0, true);
  }

  fetchPage(offset, reset) {
    const { bucket, property, order, loadMoreCount } = this.state;
    const orderBy = order === 'desc' ? `-${property}` : property;
    const limit = (!reset && loadMoreCount >= 2) ? 500 : 50;

    if (reset) {
      this.setState({ loading: true, numbers: [], loadMoreCount: 0 });
    } else {
      this.setState({ loadingMore: true });
    }

    API.GET(
      `/bridges/number-assignment/?bucket=${bucket}&offset=${offset}&limit=${limit}&order_by=${orderBy}`
    )
      .then(r => r.json())
      .then(data => {
        this.setState(prev => ({
          summary: data.summary,
          numbers: reset ? data.numbers : prev.numbers.concat(data.numbers),
          offset: data.offset,
          total: data.total,
          loadMoreCount: reset ? 0 : prev.loadMoreCount + 1,
          loading: false,
          loadingMore: false,
        }));
      })
      .catch(() => this.setState({ loading: false, loadingMore: false }));
  }

  selectBucket(key) {
    this.setState({ bucket: key, offset: 0 }, () => this.fetchPage(0, true));
  }

  handleSort(property) {
    this.setState(prev => ({
      property,
      order: prev.property === property && prev.order === 'asc' ? 'desc' : 'asc',
      offset: 0,
    }), () => this.fetchPage(0, true));
  }

  toggleSelect(phoneNumber) {
    if (!phoneNumber) return;
    this.setState(prev => {
      const next = new Set(prev.selected);
      if (next.has(phoneNumber)) next.delete(phoneNumber); else next.add(phoneNumber);
      return { selected: next };
    });
  }

  toggleSelectAll() {
    const { numbers, selected } = this.state;
    const eligible = numbers.filter(r => r.number).map(r => r.number);
    const allSelected = eligible.length > 0 && eligible.every(n => selected.has(n));
    this.setState(prev => {
      const next = new Set(prev.selected);
      if (allSelected) {
        eligible.forEach(n => next.delete(n));
      } else {
        eligible.forEach(n => next.add(n));
      }
      return { selected: next };
    });
  }

  releaseSelected() {
    const { selected, numbers } = this.state;
    // Map phone-number selection to DID ids (orphans without ids can't be released)
    const idByNumber = new Map(numbers.filter(r => r.id !== null).map(r => [r.number, r.id]));
    const ids = Array.from(selected).map(n => idByNumber.get(n)).filter(x => x !== undefined);
    if (!ids.length) return;
    if (!window.confirm('Release ' + ids.length + ' number(s) from the provider and mark as cleaned up? This cannot be undone.')) return;
    this.setState({ releasing: true });
    API.POST('/bridges/numbers-cleanup/', { ids })
      .then(r => r.json())
      .then(data => {
        this.setState({ releasing: false, selected: new Set() });
        this.fetchPage(0, true);
      })
      .catch(() => this.setState({ releasing: false }));
  }

  openHoldDialog() {
    if (this.state.selected.size === 0) return;
    this.setState({ holdDialogOpen: true });
  }

  closeHoldDialog() {
    this.setState({ holdDialogOpen: false });
  }

  holdSelected() {
    const numbers = Array.from(this.state.selected);
    const reason = this.state.holdReason;
    const comment = this.state.holdComment;
    if (!numbers.length) return;
    this.setState({ holding: true });
    API.POST('/bridges/numbers-hold/', { numbers: numbers, reason: reason, comment: comment })
      .then(r => r.json())
      .then(data => {
        this.setState({
          holding: false,
          holdDialogOpen: false,
          holdComment: '',
          selected: new Set(),
        });
        this.fetchPage(0, true);
      })
      .catch(() => this.setState({ holding: false }));
  }

  unholdSelected() {
    const numbers = Array.from(this.state.selected);
    if (!numbers.length) return;
    if (!window.confirm('Remove ' + numbers.length + ' number(s) from the hold list? They will become eligible for cleanup again.')) return;
    this.setState({ holding: true });
    fetch(window.location.origin + '/api/v2/bridges/numbers-hold/', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ numbers: numbers }),
    })
      .then(r => r.json())
      .then(data => {
        this.setState({ holding: false, selected: new Set() });
        this.fetchPage(0, true);
      })
      .catch(() => this.setState({ holding: false }));
  }

  render() {
    const { summary, numbers, bucket, loading, loadingMore, property, order, selected, releasing,
            holding, holdDialogOpen, holdReason, holdComment } = this.state;
    const eligibleNumbers = numbers.filter(r => r.number).map(r => r.number);
    const allPageSelected = eligibleNumbers.length > 0 && eligibleNumbers.every(n => selected.has(n));
    const isHeldBucket = bucket === 'held';

    const activeBucket = BUCKETS.find(b => b.key === bucket);

    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <span style={styles.title}>Number Assignment</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {summary && (
              <span style={{ fontSize: '12px', color: mid.text1 }}>
                <span style={{ color: mid.cyan, fontWeight: 600 }}>{summary.total}</span>
                {' '}numbers in provider inventory
              </span>
            )}
            {selected.size > 0 && !isHeldBucket && (
              <Button
                variant="raised"
                onClick={() => this.openHoldDialog()}
                disabled={holding}
                style={{
                  backgroundColor: holding ? mid.bg2 : mid.cyan,
                  color: '#fff',
                  fontSize: '12px',
                  height: '30px',
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {holding ? 'Holding…' : 'Hold ' + selected.size + ' number' + (selected.size === 1 ? '' : 's')}
              </Button>
            )}
            {selected.size > 0 && isHeldBucket && (
              <Button
                variant="raised"
                onClick={() => this.unholdSelected()}
                disabled={holding}
                style={{
                  backgroundColor: holding ? mid.bg2 : mid.amber,
                  color: '#fff',
                  fontSize: '12px',
                  height: '30px',
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {holding ? 'Unholding…' : 'Unhold ' + selected.size + ' number' + (selected.size === 1 ? '' : 's')}
              </Button>
            )}
            {selected.size > 0 && !isHeldBucket && (
              <Button
                variant="raised"
                onClick={() => this.releaseSelected()}
                disabled={releasing}
                style={{
                  backgroundColor: releasing ? mid.bg2 : mid.red,
                  color: '#fff',
                  fontSize: '12px',
                  height: '30px',
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {releasing ? 'Releasing…' : 'Release & Clean Up ' + selected.size + ' number' + (selected.size === 1 ? '' : 's')}
              </Button>
            )}
          </div>
        </div>

        <div style={styles.bucketBar}>
          {BUCKETS.map(b => (
            <div
              key={b.key}
              style={styles.bucketCard(bucket === b.key, b)}
              onClick={() => this.selectBucket(b.key)}
            >
              <div style={styles.bucketCount(b.color)}>
                {summary ? (b.key === 'all' ? summary.total : summary[b.key]) : '—'}
              </div>
              <div style={styles.bucketLabel}>{b.label}</div>
              <div style={styles.bucketDesc}>{b.desc}</div>
            </div>
          ))}
        </div>

        {loading && <Loading />}

        {!loading && numbers.length === 0 && (
          <div style={styles.empty}>
            No numbers in the <strong>{activeBucket && activeBucket.label}</strong> bucket.
          </div>
        )}

        {!loading && numbers.length > 0 && (
          <div style={styles.tableWrap}>
            <Table>
              <TableHead>
                <TableRow style={{ backgroundColor: mid.bg2 }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allPageSelected}
                      onChange={() => this.toggleSelectAll()}
                      style={{ color: allPageSelected ? mid.blue : mid.text2 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={property === 'number'}
                      direction={property === 'number' ? order : 'asc'}
                      onClick={() => this.handleSort('number')}
                    >
                      Number
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={property === 'route_to__company__name'}
                      direction={property === 'route_to__company__name' ? order : 'asc'}
                      onClick={() => this.handleSort('route_to__company__name')}
                    >
                      Company
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Last Paid</TableCell>
                  <TableCell>Last Call</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={property === 'created'}
                      direction={property === 'created' ? order : 'asc'}
                      onClick={() => this.handleSort('created')}
                    >
                      Added
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Health</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {numbers.map((row, i) => {
                  const rowBucket = bucketForRow(row);
                  const chip = BUCKET_CHIP[rowBucket] || { label: rowBucket, color: mid.text2 };
                  const company = row.company;
                  const isStale = row.last_paid
                    ? new Date(row.last_paid) < new Date(new Date().getFullYear(), 0, 1)
                    : true;

                  return (
                    <TableRow
                      key={i}
                      style={{ backgroundColor: selected.has(row.id) ? 'rgba(37,99,235,0.10)' : mid.bg1 }}
                    >
                      <TableCell padding="checkbox">
                        {row.number && (
                          <Checkbox
                            checked={selected.has(row.number)}
                            onChange={() => this.toggleSelect(row.number)}
                            style={{ color: selected.has(row.number) ? mid.blue : mid.text2 }}
                          />
                        )}
                      </TableCell>
                      <TableCell style={{ color: mid.cyan, fontFamily: 'monospace', fontSize: '12px' }}>
                        {row.number}
                      </TableCell>
                      <TableCell style={{ color: mid.text1, fontSize: '11px' }}>
                        {row.billing_provider || '—'}
                      </TableCell>
                      <TableCell>
                        {company
                          ? (
                            <a
                              style={{ color: mid.blue, cursor: 'pointer' }}
                              onClick={() => this.props.history.push(`/company-detail/${company.id}`)}
                            >
                              {company.name}
                            </a>
                          )
                          : <span style={{ color: mid.text2 }}>—</span>
                        }
                      </TableCell>
                      <TableCell>
                        {company
                          ? (
                            <Chip
                              label={company.status}
                              style={{
                                height: '20px',
                                fontSize: '10px',
                                backgroundColor:
                                  company.status === 'subscribed' ? 'rgba(22,163,74,0.16)'
                                  : company.status === 'pending'  ? 'rgba(180,83,9,0.16)'
                                  :                                  'rgba(31,32,36,0.08)',
                                color:
                                  company.status === 'subscribed' ? mid.green
                                  : company.status === 'pending'  ? mid.amber
                                  :                                  mid.text1,
                              }}
                            />
                          )
                          : <span style={{ color: mid.text2, fontSize: '11px' }}>no company</span>
                        }
                      </TableCell>
                      <TableCell style={{ color: company && company.tariff ? mid.text0 : mid.text2, fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {company ? planLabel(company.tariff) : '—'}
                      </TableCell>
                      <TableCell style={{
                        fontSize: '11px',
                        color: isStale ? mid.amber : mid.text1,
                        fontWeight: isStale ? 600 : 'normal',
                        whiteSpace: 'nowrap',
                      }}>
                        {row.last_paid
                          ? <Moment format="YYYY-MM-DD">{row.last_paid}</Moment>
                          : <span style={{ color: mid.red }}>never</span>
                        }
                      </TableCell>
                      <TableCell style={{ fontSize: '11px', color: mid.text1, whiteSpace: 'nowrap' }}>
                        {row.last_call
                          ? <Moment format="YYYY-MM-DD">{row.last_call}</Moment>
                          : <span style={{ color: mid.text2 }}>—</span>
                        }
                      </TableCell>
                      <TableCell style={{ fontSize: '11px', color: mid.text2 }}>
                        {row.created ? <Moment format="YYYY-MM-DD">{row.created}</Moment> : <span style={{ color: mid.text2 }}>—</span>}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={chip.label}
                          style={{
                            height: '20px',
                            fontSize: '10px',
                            backgroundColor: chip.color + '22',
                            color: chip.color,
                          }}
                        />
                      </TableCell>
                      <TableCell style={{ fontSize: '11px', color: mid.text1, whiteSpace: 'nowrap' }}>
                        {row.reason || '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {loadingMore && (
          <div style={styles.loadingMore}>Loading more…</div>
        )}

        {holdDialogOpen && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              backgroundColor: mid.bg1,
              border: '1px solid ' + mid.border,
              borderRadius: '8px',
              padding: '24px',
              minWidth: '420px',
              maxWidth: '560px',
            }}>
              <div style={{ fontSize: '15px', fontWeight: 600, color: mid.text0, marginBottom: '12px' }}>
                Hold {selected.size} number{selected.size === 1 ? '' : 's'}
              </div>
              <div style={{ fontSize: '12px', color: mid.text1, marginBottom: '16px' }}>
                Holding excludes these numbers from the Cleanup Ready bucket and any other
                cleanup workflows. They'll appear in the Held bucket until you Unhold them.
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '11px', color: mid.text2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Reason
                </label>
                <select
                  value={holdReason}
                  onChange={e => this.setState({ holdReason: e.target.value })}
                  style={{
                    display: 'block', marginTop: '6px',
                    width: '100%', padding: '8px',
                    backgroundColor: mid.bg0, color: mid.text0,
                    border: '1px solid ' + mid.border, borderRadius: '4px',
                    fontSize: '12px',
                  }}
                >
                  <option value="test">Test / regression</option>
                  <option value="team_use">Team / internal use</option>
                  <option value="future_block">Future-block reserve</option>
                  <option value="regression">Regression-test fixture</option>
                  <option value="other">Other (see comment)</option>
                </select>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '11px', color: mid.text2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Comment (optional)
                </label>
                <textarea
                  value={holdComment}
                  onChange={e => this.setState({ holdComment: e.target.value })}
                  rows={3}
                  style={{
                    display: 'block', marginTop: '6px',
                    width: '100%', padding: '8px',
                    backgroundColor: mid.bg0, color: mid.text0,
                    border: '1px solid ' + mid.border, borderRadius: '4px',
                    fontSize: '12px', fontFamily: 'inherit', resize: 'vertical',
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <Button
                  onClick={() => this.closeHoldDialog()}
                  disabled={holding}
                  style={{ color: mid.text1, fontSize: '12px', textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="raised"
                  onClick={() => this.holdSelected()}
                  disabled={holding}
                  style={{
                    backgroundColor: holding ? mid.bg2 : mid.cyan,
                    color: '#fff', fontSize: '12px',
                    textTransform: 'none', fontWeight: 600,
                  }}
                >
                  {holding ? 'Holding…' : 'Hold'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withRouter(NumberAssignment);



// WEBPACK FOOTER //
// ./src/pages/NumberAssignment.js